import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api, { UPLOAD_TIMEOUT } from '../lib/api';
import { compressImage } from '../lib/imageCompression';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Upload, CheckCircle, Clock, XCircle, Shield, Phone } from 'lucide-react';
import { toast } from 'sonner';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';

const VerifyID = ({ profile, setProfile }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // All accounts: 3 separate uploads (Front ID, Back ID, Photo with ID)
  const [idFrontUrl, setIdFrontUrl] = useState('');
  const [idBackUrl, setIdBackUrl] = useState('');
  const [idSelfieUrl, setIdSelfieUrl] = useState('');
  const [uploadingIdFront, setUploadingIdFront] = useState(false);
  const [uploadingIdBack, setUploadingIdBack] = useState(false);
  const [uploadingIdSelfie, setUploadingIdSelfie] = useState(false);

  useEffect(() => {
    checkVerificationStatus();
  }, []);

  const checkVerificationStatus = async () => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const response = await api.get('/api/verification/status', {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });

      setVerificationStatus(response.data);
    } catch (error) {
      console.error('Failed to check verification status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event, documentType) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && file.type !== 'application/octet-stream') {
      toast.error(t('verification.selectImageFile'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('verification.imageTooLarge'));
      return;
    }

    // Set appropriate loading state
    if (documentType === 'id_front') setUploadingIdFront(true);
    else if (documentType === 'id_back') setUploadingIdBack(true);
    else if (documentType === 'id_selfie') setUploadingIdSelfie(true);

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) throw new Error('Not authenticated');

      // Compress image before uploading (document preset: 2048px, 1.5MB)
      const compressed = await compressImage(file, 'document');

      // Upload via backend API (supports HEIC/HEIF)
      const formData = new FormData();
      formData.append('file', compressed);
      formData.append('document_type', documentType);

      const response = await api.post('/api/upload/id-document', formData, {
        headers: {
          'Authorization': `Bearer ${session.session.access_token}`
        },
        timeout: UPLOAD_TIMEOUT,
        params: { document_type: documentType }
      });

      const filePath = response.data.file_path;
      
      // Set appropriate state based on document type
      if (documentType === 'id_front') setIdFrontUrl(filePath);
      else if (documentType === 'id_back') setIdBackUrl(filePath);
      else if (documentType === 'id_selfie') setIdSelfieUrl(filePath);
      
      toast.success('Document uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to upload document');
    } finally {
      if (documentType === 'id_front') setUploadingIdFront(false);
      else if (documentType === 'id_back') setUploadingIdBack(false);
      else if (documentType === 'id_selfie') setUploadingIdSelfie(false);
    }
  };

  const handleSubmit = async () => {
    // Validate all 3 uploads
    if (!idFrontUrl || !idBackUrl || !idSelfieUrl) {
      toast.error(t('verification.uploadAll3Required'));
      return;
    }

    setSubmitting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('Not authenticated');
      }

      // All accounts use same payload structure
      const payload = {
        id_front_url: idFrontUrl,
        id_back_url: idBackUrl,
        selfie_url: idSelfieUrl,
        id_type: 'government_id'
      };

      await api.post(
        '/api/verification/request',
        payload,
        {
          headers: {
            Authorization: `Bearer ${session.session.access_token}`
          }
        }
      );

      toast.success(t('verification.verificationSubmitted'));
      checkVerificationStatus();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || t('verification.failedToSubmit'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="flex justify-center items-center h-96">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe pb-nav md:pb-0">
      <TopBar profile={profile} setProfile={setProfile} />

      <div className="px-4 py-6 max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="font-heading text-3xl font-bold mb-2">{t('verification.idVerificationTitle')}</h1>
          <p className="text-muted-foreground">
            {t('verification.verifyIdentityDesc')}
          </p>
        </div>

        {verificationStatus?.status === 'verified' && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              {t('verification.idVerifiedFull')}
            </AlertDescription>
          </Alert>
        )}

        {verificationStatus?.status === 'pending' && (
          <Alert className="bg-yellow-50 border-yellow-200">
            <Clock className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              {t('verification.pendingReview')}
            </AlertDescription>
          </Alert>
        )}

        {verificationStatus?.status === 'rejected' && (
          <Alert className="bg-red-50 border-red-200">
            <XCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              {t('verification.verificationRejected')} {verificationStatus.request?.rejection_reason || t('verification.tryAgainClear')}
            </AlertDescription>
          </Alert>
        )}

        {(verificationStatus?.status === 'not_requested' || verificationStatus?.status === 'rejected') && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="text-primary" size={24} />
                {t('verification.submitIdVerification')}
              </CardTitle>
              <CardDescription>
                {t('verification.upload3Photos')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* All Accounts - 3 Separate Uploads */}
              <>
                  {/* ID Front Upload */}
                  <div className="space-y-2">
                    <Label>1. {t('verification.frontOfId')} *</Label>
                    <div className="border-2 border-dashed rounded-xl p-6 text-center hover:border-primary transition-colors">
                      {idFrontUrl ? (
                        <div className="space-y-2">
                          <CheckCircle className="mx-auto text-green-500" size={36} />
                          <p className="text-sm text-green-600 font-medium">{t('verification.uploaded')}</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIdFrontUrl('')}
                            className="text-xs"
                          >
                            {t('verification.change')}
                          </Button>
                        </div>
                      ) : (
                        <label htmlFor="id-front-upload" className="cursor-pointer">
                          <Upload className="mx-auto mb-2 text-muted-foreground" size={36} />
                          <p className="text-sm font-medium">{t('verification.uploadFrontOfId')}</p>
                          <p className="text-xs text-muted-foreground">{t('verification.pngJpgUpTo10mb')}</p>
                          <input
                            id="id-front-upload"
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileUpload(e, 'id_front')}
                            className="hidden"
                            disabled={uploadingIdFront}
                            data-testid="upload-id-front"
                          />
                          {uploadingIdFront && <p className="text-xs text-primary mt-2">{t('verification.uploading')}</p>}
                        </label>
                      )}
                    </div>
                  </div>
                  
                  {/* ID Back Upload */}
                  <div className="space-y-2">
                    <Label>2. {t('verification.backOfId')} *</Label>
                    <div className="border-2 border-dashed rounded-xl p-6 text-center hover:border-primary transition-colors">
                      {idBackUrl ? (
                        <div className="space-y-2">
                          <CheckCircle className="mx-auto text-green-500" size={36} />
                          <p className="text-sm text-green-600 font-medium">{t('verification.uploaded')}</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIdBackUrl('')}
                            className="text-xs"
                          >
                            {t('verification.change')}
                          </Button>
                        </div>
                      ) : (
                        <label htmlFor="id-back-upload" className="cursor-pointer">
                          <Upload className="mx-auto mb-2 text-muted-foreground" size={36} />
                          <p className="text-sm font-medium">{t('verification.uploadBackOfId')}</p>
                          <p className="text-xs text-muted-foreground">{t('verification.pngJpgUpTo10mb')}</p>
                          <input
                            id="id-back-upload"
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileUpload(e, 'id_back')}
                            className="hidden"
                            disabled={uploadingIdBack}
                            data-testid="upload-id-back"
                          />
                          {uploadingIdBack && <p className="text-xs text-primary mt-2">{t('verification.uploading')}</p>}
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Photo with ID Upload */}
                  <div className="space-y-2">
                    <Label>3. {t('verification.selfieHoldingId')} *</Label>
                    <div className="border-2 border-dashed rounded-xl p-6 text-center hover:border-primary transition-colors">
                      {idSelfieUrl ? (
                        <div className="space-y-2">
                          <CheckCircle className="mx-auto text-green-500" size={36} />
                          <p className="text-sm text-green-600 font-medium">{t('verification.uploaded')}</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIdSelfieUrl('')}
                            className="text-xs"
                          >
                            {t('verification.change')}
                          </Button>
                        </div>
                      ) : (
                        <label htmlFor="id-selfie-upload" className="cursor-pointer">
                          <Upload className="mx-auto mb-2 text-muted-foreground" size={36} />
                          <p className="text-sm font-medium">{t('verification.uploadSelfieHoldingId')}</p>
                          <p className="text-xs text-muted-foreground">{t('verification.holdIdNextToFace')}</p>
                          <input
                            id="id-selfie-upload"
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileUpload(e, 'id_selfie')}
                            className="hidden"
                            disabled={uploadingIdSelfie}
                            data-testid="upload-id-selfie"
                          />
                          {uploadingIdSelfie && <p className="text-xs text-primary mt-2">{t('verification.uploading')}</p>}
                        </label>
                      )}
                    </div>
                  </div>

                  <Alert>
                    <AlertDescription className="text-sm">
                      <strong>{t('verification.verificationRequirements')}</strong>
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>{t('verification.clearPhotoFront')}</li>
                        <li>{t('verification.clearPhotoBack')}</li>
                        <li>{t('verification.selfieWithId')}</li>
                        <li>{t('verification.documentsVisible')}</li>
                      </ul>
                    </AlertDescription>
                  </Alert>
              </>

              {(profile?.role === 'employer' || profile?.role === 'owner') && (
                <Alert className="border-blue-200 bg-blue-50">
                  <Phone className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-sm">
                    <strong>{t('verification.phoneCallNotice', 'Phone Verification Required')}</strong>
                    <p className="mt-1">{t('verification.phoneCallDescription', 'After submitting your documents, our team will call the phone number on your profile to verify you are authorized to manage jobs for this business. Please ensure your phone number is correct.')}</p>
                    {profile?.phone ? (
                      <p className="mt-2 font-medium">{t('verification.yourPhone', 'Your phone')}: {profile.phone}</p>
                    ) : (
                      <p className="mt-2 text-red-600 font-medium">{t('verification.phoneRequired', 'Please add a phone number to your profile before submitting.')}</p>
                    )}
                    <p className="mt-2 text-blue-700 text-xs">{t('verification.fieldLockNotice', 'Note: After verification, your business name and location will be locked and cannot be changed without contacting support.')}</p>
                  </AlertDescription>
                </Alert>
              )}

              <Button
                onClick={handleSubmit}
                disabled={submitting || !idFrontUrl || !idBackUrl || !idSelfieUrl || ((profile?.role === 'employer' || profile?.role === 'owner') && !profile?.phone)}
                className="w-full rounded-xl"
                size="lg"
              >
                {submitting ? t('verification.submitting') : t('verification.submitForVerification')}
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg font-semibold mb-3">{t('verification.whyVerify')}</h3>
            <ul className="space-y-2 text-sm">
              {(profile?.role === 'employer' || profile?.role === 'owner') ? (
                <>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('verification.postJobsGigs')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('verification.buildTrustBadge')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('verification.accessPremium')}</span>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('verification.showVerifiedBadge')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('verification.increaseChances3x')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('verification.standOutTrusted')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('verification.priorityVisibility')}</span>
                  </li>
                </>
              )}
            </ul>
          </CardContent>
        </Card>
      </div>

      <BottomNav profile={profile} />
    </div>
  );
};

export default VerifyID;
